import React, { useRef } from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';
import { content } from '../../data/content';
import { useAnimationVariants } from '../../hooks/useAnimationVariants';
import { useParallax } from '../../hooks/useParallax';

const MotionBox = motion.create(Box);

// Tilt for stat cards and image
const useTiltCard = (maxRot = 6) => {
  const ref = useRef<HTMLDivElement>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [maxRot, -maxRot]), { stiffness: 280, damping: 28 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-maxRot, maxRot]), { stiffness: 280, damping: 28 });
  const gX   = useTransform(mx, [-0.5, 0.5], [0, 100]);
  const gY   = useTransform(my, [-0.5, 0.5], [0, 100]);
  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };
  return { ref, rotX, rotY, gX, gY, onMove, onLeave };
};

const STAT_GRADIENTS = [
  'linear-gradient(135deg, #818CF8, #a78bfa)',
  'linear-gradient(135deg, #a78bfa, #34d399)',
  'linear-gradient(135deg, #34d399, #818CF8)',
];

interface StatCardProps {
  stat: { value: string; label: string; sub: string };
  gradient: string;
  index: number;
}

const StatCard: React.FC<StatCardProps> = ({ stat, gradient, index }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const t = useTiltCard(6);

  return (
    <Box
      ref={t.ref}
      onMouseMove={t.onMove}
      onMouseLeave={t.onLeave}
      component={motion.div}
      style={{ rotateX: t.rotX, rotateY: t.rotY, transformStyle: 'preserve-3d' } as any}
      sx={{ perspective: '800px' }}
    >
      <Box
        sx={{
          p: 3, minWidth: 140, borderRadius: '14px', textAlign: 'center',
          position: 'relative', overflow: 'hidden',
          border: `1px solid ${theme.palette.primary.main}22`,
          bgcolor: isDark ? 'rgba(129,140,248,0.06)' : 'rgba(99,102,241,0.04)',
          backdropFilter: 'blur(16px)',
          boxShadow: 'var(--shadow-card)',
          transition: 'border-color 300ms ease, box-shadow 300ms ease',
          cursor: 'default',
          '&:hover': {
            borderColor: `${theme.palette.primary.main}55`,
            boxShadow: `var(--glow-primary), 0 4px 20px rgba(0,0,0,0.2)`,
          },
          '&::before': {
            content: '""', position: 'absolute', top: 0, left: 0, right: 0,
            height: '2px', background: gradient, borderRadius: '2px 2px 0 0',
          },
        }}
      >
        {/* Dynamic spotlight from mouse */}
        <Box
          component={motion.div}
          style={{
            background: `radial-gradient(circle at ${t.gX}% ${t.gY}%, rgba(129,140,248,0.1), transparent 60%)`,
          } as any}
          sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: '14px' }}
        />
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, lineHeight: 1, background: gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
          {stat.value}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.25, fontSize: '0.8125rem' }}>
          {stat.label}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6875rem' }}>
          {stat.sub}
        </Typography>
      </Box>
    </Box>
  );
};

const About: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { overline, title, description, stats, image } = content.about;
  const { fadeUp, stagger } = useAnimationVariants();

  const sectionRef = useRef<HTMLDivElement>(null);
  const textParallax  = useParallax({ outputRange: [30, -30] });
  const imageParallax = useParallax({ outputRange: [-30, 30] });
  const imageTilt     = useTiltCard(8);

  const titleParts = title.split('\n');

  return (
    <Box id="about" ref={sectionRef} sx={{ py: { xs: 10, md: 16 }, bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
      {/* Accent orb */}
      <Box sx={{ position: 'absolute', top: '-20%', left: '-10%', width: '50%', paddingBottom: '50%', borderRadius: '50%', background: `radial-gradient(circle, ${theme.palette.primary.main}0C 0%, transparent 70%)`, pointerEvents: 'none', zIndex: 0 }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">

          {/* Left: Text */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box ref={textParallax.ref}>
              <MotionBox style={{ y: textParallax.y }}>
                <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }}>

                  <MotionBox variants={fadeUp}>
                    <Typography variant="overline" sx={{ color: 'primary.main', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.14em', mb: 2, display: 'block' }}>
                      {overline}
                    </Typography>
                  </MotionBox>

                  <MotionBox variants={fadeUp} sx={{ mb: 3.5 }}>
                    <Typography variant="h2" component="h2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      {titleParts[0]}{' '}
                      <Box component="span" sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main}, #a78bfa)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                        {titleParts[1]}
                      </Box>
                    </Typography>
                  </MotionBox>

                  <MotionBox variants={fadeUp}>
                    {description.split('\n\n').map((para, i) => (
                      <Typography key={i} variant="body1" component="p" sx={{ color: 'text.secondary', lineHeight: 1.85, mb: 2, fontSize: '1.025rem' }}>
                        {para}
                      </Typography>
                    ))}
                  </MotionBox>

                  {/* Stat cards with tilt */}
                  <MotionBox variants={stagger} sx={{ display: 'flex', gap: 2, mt: 5, flexWrap: 'wrap' }}>
                    {stats.map((stat, i) => (
                      <MotionBox key={stat.label} variants={fadeUp}>
                        <StatCard stat={stat} gradient={STAT_GRADIENTS[i]} index={i} />
                      </MotionBox>
                    ))}
                  </MotionBox>

                </MotionBox>
              </MotionBox>
            </Box>
          </Grid>

          {/* Right: Image with tilt */}
          <Grid size={{ xs: 12, md: 5 }} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Box ref={imageParallax.ref} sx={{ width: '100%', maxWidth: 360, position: 'relative' }}>
              <MotionBox
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
                style={{ y: imageParallax.y }}
              >
                <Box
                  ref={imageTilt.ref}
                  onMouseMove={imageTilt.onMove}
                  onMouseLeave={imageTilt.onLeave}
                  component={motion.div}
                  style={{ rotateX: imageTilt.rotX, rotateY: imageTilt.rotY, transformStyle: 'preserve-3d' } as any}
                  sx={{ position: 'relative', perspective: '1000px' }}
                >
                  {/* Rotating gradient ring */}
                  <Box sx={{ position: 'absolute', inset: -4, borderRadius: '16px', background: `linear-gradient(135deg, ${theme.palette.primary.main}44, #a78bfa33, transparent)`, zIndex: 0, animation: 'gradient-rotate 12s linear infinite' }} />

                  <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1/1', borderRadius: '14px', overflow: 'hidden', bgcolor: 'background.default', zIndex: 1, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
                    <Box component="img" src={image} alt="Sajjad Khalid — React Native Engineer building production mobile applications" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </Box>

                  {/* "Available for Work" badge */}
                  <Box sx={{ position: 'absolute', top: -14, right: -14, zIndex: 3, bgcolor: isDark ? 'rgba(13,17,23,0.92)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)', border: `1px solid ${theme.palette.divider}`, borderRadius: '20px', px: 1.75, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#34d399', boxShadow: '0 0 8px rgba(52,211,153,0.8)', animation: 'pulse-ring 2.2s ease-out infinite' }} />
                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.625rem', color: '#34d399', fontWeight: 500, letterSpacing: '0.06em' }}>
                      Available for Work
                    </Typography>
                  </Box>

                  {/* Spinning corner accent */}
                  <Box
                    component={motion.div}
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
                    sx={{ position: 'absolute', bottom: -14, right: -14, width: 72, height: 72, borderRadius: '12px', background: `linear-gradient(135deg, ${theme.palette.primary.main}22, #a78bfa22)`, border: `1px solid ${theme.palette.primary.main}33`, zIndex: 0 }}
                  />
                </Box>
              </MotionBox>
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default About;
